import client from 'src/api/client';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initialState, LiveInitType, LiveStatus } from './state';
import { dateFormatter } from 'src/utils/dateFormatter';
import { BASE_URL } from 'src/config/api';
import { StoreState } from 'src/admin-entity/interface/store/store-state.interface';
import { SimpleDateSelectType } from 'src/admin-entity/enum/search/simple-date-select-type.enum';
import { getStartEndDate } from 'src/utils/getStartEndDate';
import { AppThunk } from 'src/store';
import { updateHomeDisplayCategory, updateSelectedHomeDisplayCategoryStatus } from '../home-display/homeDisplaySlice';
import { getPointSetting } from 'src/api/point';
import {
    liveReserveSetting,
    reserveCartCancel as getReserveCartCancel,
    updateLiveReserveSetting,
    updateReserveCartCancel,
} from 'src/api/live/live';
import { getLsBroadcastSettingCoupons } from 'src/api/coupon2';
import { ReserveGlobalSettingsResponse } from 'src/admin-entity/interface/live/live-global-setting-reserve.interface';
import { ReserveCartCancel } from 'src/admin-entity/interface/live/live-reserve-cart-cancel';
import { LsBroadcastRegisteredCoupon } from 'src/admin-entity/interface/coupon2/coupon2-ls-broadcast-setting.interface';

export const fetchReservationPaymentSetting = createAsyncThunk(
    'liveSlice/fetchReservationPaymentSetting',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await liveReserveSetting();
            return data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const saveReservationPaymentSetting = createAsyncThunk(
    'liveSlice/saveReservationPaymentSetting',
    async (setting: ReserveGlobalSettingsResponse, { rejectWithValue }) => {
        try {
            const { data } = await updateLiveReserveSetting(
                setting.isReserveCancelEnabled,
                setting.maxReserveCancelCount,
                setting.isUnpaidPenaltyEnabled,
                setting.maxUnpaidPenaltyCount,
            );
            return data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const fetchReserveCartCancel = createAsyncThunk(
    'liveSlice/fetchReserveCartCancel',
    async (lsBroadcastId: number, { rejectWithValue }) => {
        try {
            const { data } = await getReserveCartCancel(lsBroadcastId);

            return data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const fetchPointSetting = createAsyncThunk(
    'liveSlice/fetchPointSetting',
    async (lsBroadcastId: number, { rejectWithValue }) => {
        try {
            const { data } = await getPointSetting(lsBroadcastId);
            return {
                isPointUsableForSpecialPrice: data.isPointUsableForSpecialPrice,
                isLiveRewardPointEnabled: data.isLiveRewardPointEnabled,
                liveRewardPointAmount: data.liveRewardPointAmount,
            };
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const fetchLsBroadcastCoupons = createAsyncThunk(
    'liveSlice/fetchLsBroadcastCoupons',
    async (lsBroadcastId: number, { rejectWithValue }) => {
        try {
            const { data } = await getLsBroadcastSettingCoupons(lsBroadcastId);
            return { registeredCoupons: data.registeredCoupons, isCouponUsable: data.isCouponUsable };
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const saveReserveCartCancel = createAsyncThunk(
    'liveSlice/saveReserveCartCancel',
    async ({ lsBroadcastId, data }: { lsBroadcastId: number; data: ReserveCartCancel }, { rejectWithValue }) => {
        try {
            const { data: result } = await updateReserveCartCancel(lsBroadcastId, data);
            return result;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const searchLiveList = createAsyncThunk(`liveSlice/searchLiveList`, async (_, { getState, rejectWithValue }) => {
    try {
        const { searchToken, beginDate, endDate, page, take } = (getState() as StoreState).live.searchCondition;

        const url = new URL(`${BASE_URL}/v1/admin/ls`);
        url.searchParams.append('page', `${page}`);
        url.searchParams.append('take', `${take}`);
        url.searchParams.append('searchStartDate', dateFormatter(new Date(beginDate), 'yyyy-MM-DD'));
        url.searchParams.append('searchEndDate', dateFormatter(new Date(endDate), 'yyyy-MM-DD'));

        if (searchToken) url.searchParams.append('title', searchToken);
        const res = await client.get(url.toString());
        return res.data;
    } catch (err) {
        return rejectWithValue(err);
    }
});

// 방송 시작
export const startLive = (): AppThunk => async (dispatch, getState) => {
    dispatch(liveSlice.actions.setLiveStart());

    const { homeDisplay } = getState();

    if (homeDisplay.selectedHomeDisplayCategory.displayContentsType === 'liveStreaming') {
        dispatch(updateSelectedHomeDisplayCategoryStatus(true));
        await dispatch(updateHomeDisplayCategory({}));
    }
};

// 방송 종료
export const endLive = (): AppThunk => async (dispatch, getState) => {
    dispatch(liveSlice.actions.setLiveEnd());

    const { homeDisplay } = getState();

    if (homeDisplay.selectedHomeDisplayCategory.displayContentsType === 'liveStreaming') {
        dispatch(updateSelectedHomeDisplayCategoryStatus(false));
        await dispatch(updateHomeDisplayCategory({}));
    }
};

const liveSlice = createSlice({
    name: 'live',
    initialState: initialState,
    reducers: {
        // 방송 준비 상태로 변경 (관리자에서 방송 준비 버튼 클릭)
        setLivePreparing(state: LiveInitType) {
            state.liveStatus = LiveStatus.PREPARING;
        },
        // 방송 시작 (스트리머 앱에서 방송 시작 시)
        setLiveStart(state: LiveInitType) {
            state.liveStatus = LiveStatus.LIVE;
        },
        // 방송 종료 (관리자에서 종료 또는 스트리머 앱에서 종료 이벤트)
        setLiveEnd(state: LiveInitType) {
            state.liveStatus = LiveStatus.IDLE;
        },
        // 방송 제목 업데이트
        setCurrentTitle: (state, action: PayloadAction<string>) => {
            state.currentTitle = action.payload;
        },
        setAutoAdd(state, action: PayloadAction<boolean>) {
            state.autoAdd = action.payload;
        },
        updateSearchToken: (state, action: PayloadAction<string>) => {
            state.searchCondition.searchToken = action.payload;
        },
        updateBeginDate: (state, action: PayloadAction<string>) => {
            state.searchCondition.beginDate = action.payload;
        },
        updateEndDate: (state, action: PayloadAction<string>) => {
            state.searchCondition.endDate = action.payload;
        },
        onClickSimpleDateSelector: (state, action: PayloadAction<SimpleDateSelectType>) => {
            const { startDate, endDate } = getStartEndDate(action.payload);
            state.searchCondition.beginDate = startDate.toString();
            state.searchCondition.endDate = endDate.toString();
        },
        initSearchCondition: state => {
            state.searchCondition = {
                ...initialState.searchCondition,
                beginDate: new Date(new Date().setMonth(new Date().getMonth() - 3)).toString(),
                endDate: new Date().toString(),
            };
        },
        setTake: (state, action: PayloadAction<number>) => {
            state.searchCondition.take = action.payload;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.searchCondition.page = action.payload;
        },
        setReservationPaymentSetting: (state, action: PayloadAction<ReserveGlobalSettingsResponse>) => {
            state.reservationPaymentSetting = action.payload;
        },
        setCancelLimitEnabled: (state, action: PayloadAction<boolean>) => {
            state.reservationPaymentSetting.isReserveCancelEnabled = action.payload;
        },
        setCancelCount: (state, action: PayloadAction<number>) => {
            state.reservationPaymentSetting.maxReserveCancelCount = action.payload;
        },
        setPenaltyEnabled: (state, action: PayloadAction<boolean>) => {
            state.reservationPaymentSetting.isUnpaidPenaltyEnabled = action.payload;
        },
        setPenaltyScore: (state, action: PayloadAction<number>) => {
            state.reservationPaymentSetting.maxUnpaidPenaltyCount = action.payload;
        },
        setIsBundleEnabled: (state, action: PayloadAction<boolean>) => {
            state.combinedDeliverySetting.isBundleShippingEnabled = action.payload;
        },
        setPointUsable: (state, action: PayloadAction<boolean>) => {
            state.isPointUsableForSpecialPrice = action.payload;
        },
        setLiveRewardPointEnabled: (state, action: PayloadAction<boolean>) => {
            state.isLiveRewardPointEnabled = action.payload;
        },
        setLiveRewardPointAmount: (state, action: PayloadAction<number>) => {
            state.liveRewardPointAmount = action.payload;
        },
        setRegisteredCoupons: (state, action: PayloadAction<LsBroadcastRegisteredCoupon[]>) => {
            state.registeredCoupons = action.payload;
        },
        setIsLiveCouponEnabled: (state, action: PayloadAction<boolean>) => {
            state.isLiveCouponEnabled = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(searchLiveList.fulfilled, (state, action) => {
                state.liveList = action.payload;
            })
            .addCase(fetchReservationPaymentSetting.pending, state => {
                state.reservationPaymentSettingLoading = true;
            })
            .addCase(fetchReservationPaymentSetting.fulfilled, (state, action) => {
                state.reservationPaymentSetting = action.payload;
                state.reservationPaymentSettingLoading = false;
            })
            .addCase(fetchReservationPaymentSetting.rejected, state => {
                state.reservationPaymentSettingLoading = false;
            })
            .addCase(saveReservationPaymentSetting.fulfilled, (state, action) => {
                state.reservationPaymentSetting = action.payload;
            })

            .addCase(fetchReserveCartCancel.pending, state => {
                state.reserveCartCancelLoading = true;
            })
            .addCase(fetchReserveCartCancel.fulfilled, (state, action) => {
                state.reserveCartCancel = action.payload;
                state.reserveCartCancelLoading = false;
            })
            .addCase(fetchReserveCartCancel.rejected, state => {
                state.reserveCartCancelLoading = false;
            })
            .addCase(saveReserveCartCancel.fulfilled, (state, action) => {
                state.reserveCartCancel = action.payload;
            })
            .addCase(fetchPointSetting.fulfilled, (state, action) => {
                state.isPointUsableForSpecialPrice = action.payload?.isPointUsableForSpecialPrice ?? false;
                state.isLiveRewardPointEnabled = action.payload?.isLiveRewardPointEnabled ?? false;
                state.liveRewardPointAmount = action.payload?.liveRewardPointAmount ?? 0;
            })
            .addCase(fetchLsBroadcastCoupons.fulfilled, (state, action) => {
                state.registeredCoupons = action.payload.registeredCoupons;
                state.isLiveCouponEnabled = action.payload.isCouponUsable;
            });
    },
});

export const {
    updateSearchToken,
    updateBeginDate,
    updateEndDate,
    onClickSimpleDateSelector,
    initSearchCondition,
    setTake,
    setPage,
    setLivePreparing,
    setLiveStart,
    setLiveEnd,
    setCurrentTitle,
    setAutoAdd,
    setReservationPaymentSetting,
    setCancelLimitEnabled,
    setCancelCount,
    setPenaltyEnabled,
    setPenaltyScore,
    setIsBundleEnabled,
    setPointUsable,
    setLiveRewardPointEnabled,
    setLiveRewardPointAmount,
    setRegisteredCoupons,
    setIsLiveCouponEnabled,
} = liveSlice.actions;

export default liveSlice.reducer;
